/**
 * AuthProvider — the single source of truth for identity + RBAC on the client.
 *
 * Subscribes ONCE (app-wide) to onAuthStateChanged, reads the RBAC custom claims
 * from the verified ID token via getIdTokenResult, and exposes role/permission/
 * active gates. Authorization comes from the token CLAIMS, never from a Firestore
 * read (the employees/{uid} doc is fetched for profile display only).
 *
 * Offline-tolerant (P1 spec §6.1 / H5): if a forced token refresh fails (offline),
 * it falls back to the cached token and keeps the cached claims in a `degraded`
 * state rather than logging the user out.
 *
 * Additive: existing pages keep using useAuth() (re-implemented over this context
 * in src/hooks/useAuth.ts), so nothing downstream breaks.
 */
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from 'react';
import {
  User,
  onAuthStateChanged,
  getIdToken,
  getIdTokenResult,
  ParsedToken,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firestore';
import {
  AppRole,
  ORG_ID,
  isAppRole,
  permsHave,
  roleAtLeast as rankRoleAtLeast,
} from '../rbac';

/** Scanner sub-permission projection carried in the claim as `scan`. */
export interface ScanClaims {
  v: boolean; // allowValidation
  g: boolean; // allowGlobalView
  e: boolean; // allowExport
}

/** The RBAC custom claims, mirrored from functions/src/employees/types.ts. */
export interface AuthClaims {
  orgId: string;
  role: AppRole;
  perms: string[];
  scan: ScanClaims;
  active: boolean;
  cv: number;
}

/** employees/{uid} subset used for profile display only (never authorization). */
export interface EmployeeProfile {
  uid: string;
  email: string;
  displayName?: string;
  photoURL?: string | null;
  phone?: string | null;
  role?: AppRole;
  status?: string;
  claimsVersion?: number;
  scannerPermissions?: {
    allowValidation: boolean;
    allowGlobalView: boolean;
    allowExport: boolean;
  };
}

export interface AuthContextValue {
  user: User | null;
  claims: AuthClaims | null;
  profile: EmployeeProfile | null;
  role: AppRole | null;
  perms: string[];
  active: boolean;
  loading: boolean;
  /** True when running on cached claims because the network refresh failed. */
  degraded: boolean;
  /** roleAtLeast — does the user's role meet or exceed `min`? */
  hasRole: (min: AppRole) => boolean;
  /** Does the user hold permission `p` (or the 'all' wildcard) AND is active? */
  hasPerm: (p: string) => boolean;
  /** Scanner sub-permission gate. */
  canScan: (flag: keyof ScanClaims) => boolean;
  /** Force a token refresh to pick up new claims. Returns success. */
  refresh: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/** Resolve a promise or reject after `ms` so offline never hangs the app. */
function withTimeout<T>(p: Promise<T>, ms: number, label: string): Promise<T> {
  return Promise.race([
    p,
    new Promise<T>((_, reject) =>
      setTimeout(() => reject(new Error(`${label} timed out`)), ms)
    ),
  ]);
}

/** Defensively parse RBAC claims out of a verified ID token. */
function parseClaims(raw: ParsedToken | undefined | null): AuthClaims | null {
  if (!raw) return null;
  const role = raw.role;
  if (!isAppRole(role)) return null; // no RBAC claims yet (e.g. pre-migration)
  const scanRaw = (raw.scan || {}) as Record<string, unknown>;
  return {
    orgId: typeof raw.orgId === 'string' ? raw.orgId : ORG_ID,
    role,
    perms: Array.isArray(raw.perms) ? (raw.perms as string[]) : [],
    scan: {
      v: scanRaw.v === true,
      g: scanRaw.g === true,
      e: scanRaw.e === true,
    },
    active: raw.active === true,
    cv: typeof raw.cv === 'number' ? raw.cv : 0,
  };
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [claims, setClaims] = useState<AuthClaims | null>(null);
  const [profile, setProfile] = useState<EmployeeProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [degraded, setDegraded] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      if (!fbUser) {
        setUser(null);
        setClaims(null);
        setProfile(null);
        setDegraded(false);
        setLoading(false);
        return;
      }

      setUser(fbUser);

      // 1) Source of truth = ID-token claims. Force refresh, fall back to cache.
      let tokenClaims: AuthClaims | null = null;
      let degradedNow = false;
      try {
        const result = await withTimeout(getIdTokenResult(fbUser, true), 8000, 'Token refresh');
        tokenClaims = parseClaims(result.claims);
      } catch {
        // Offline / refresh failure — keep the cached token's claims (H5).
        try {
          const cached = await getIdTokenResult(fbUser, false);
          tokenClaims = parseClaims(cached.claims);
          degradedNow = true;
        } catch {
          tokenClaims = null;
        }
      }
      setClaims(tokenClaims);
      setDegraded(degradedNow);

      // 2) Profile fetch is display-only and best-effort (skip silently offline).
      try {
        const snap = await withTimeout(
          getDoc(doc(db, 'employees', fbUser.uid)),
          8000,
          'Profile fetch'
        );
        if (snap.exists()) {
          const data = snap.data() as EmployeeProfile;
          setProfile({ ...data, uid: fbUser.uid });

          // 3) Stale-claim reconciliation: doc.claimsVersion ahead of token cv.
          if (!degradedNow && typeof data.claimsVersion === 'number') {
            const tokenCv = tokenClaims?.cv ?? 0;
            if (data.claimsVersion > tokenCv) {
              try {
                await getIdToken(fbUser, true);
                const refreshed = await getIdTokenResult(fbUser, true);
                setClaims(parseClaims(refreshed.claims));
              } catch {
                /* leave a "please re-login" surface to the UI; keep cached */
              }
            }
          }
        } else {
          setProfile(null);
        }
      } catch {
        // Offline: retain whatever profile we already had.
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const refresh = useCallback(async (): Promise<boolean> => {
    const current = auth.currentUser;
    if (!current) return false;
    try {
      const result = await getIdTokenResult(current, true);
      setClaims(parseClaims(result.claims));
      setDegraded(false);
      return true;
    } catch {
      return false;
    }
  }, []);

  const value = useMemo<AuthContextValue>(() => {
    const role = claims?.role ?? null;
    const perms = claims?.perms ?? [];
    const active = claims?.active === true;
    return {
      user,
      claims,
      profile,
      role,
      perms,
      active,
      loading,
      degraded,
      hasRole: (min: AppRole) => active && rankRoleAtLeast(role, min),
      // super_admin implicitly holds EVERY permission — by role, not by relying on
      // the `'all'` token being present in the claim. A genuine super_admin whose
      // claims carry a stale/legacy perms list (no `'all'`) must never be told
      // "you don't have permission"; that's the highest role. The server already
      // grants super_admin `['all']`, so this only makes the client UI robust to a
      // slightly-stale token — it can't over-grant (the role claim is server-signed).
      hasPerm: (p: string) => active && (role === 'super_admin' || permsHave(perms, p)),
      canScan: (flag: keyof ScanClaims) =>
        active &&
        (role === 'super_admin' ||
          (permsHave(perms, 'scanner') && claims?.scan?.[flag] === true)),
      refresh,
    };
  }, [user, claims, profile, loading, degraded, refresh]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/** Access the full auth context. Throws if used outside <AuthProvider>. */
export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (ctx === undefined) {
    throw new Error('useAuthContext must be used within <AuthProvider>');
  }
  return ctx;
}

export default AuthProvider;
